// BoxAndWhiskerChart.js
// Renders the chart it is given. Saved display settings (title, axes, colours)
// are applied by the caller — applyChartSettings, with the settings of the
// version the shown run executed — so this component reads nothing itself.
//
// The figure (traces + layout) comes from a pure builder so the combined
// chart's whole-image export can draw this chart without rendering it.
import React from 'react';
import Plot from 'react-plotly.js';
import { useTheme } from '@mui/material/styles';
import ChartCard from './ChartCard';
import ChartExportButton from './ChartExportButton';
import { downloadChartPng, exportTitle } from '../../utils/chartExport';
import {
  CHART_CONFIG,
  buildAxes,
  buildLayout,
  seriesColor,
  withAlpha,
} from '../../utils/chartTheme';

export const buildBoxFigure = (chartData, { ink, themePalette }) => {
  const palette = chartData.colors || themePalette;
  const showGrid = chartData.grid !== false;

  const data = chartData.elements.map((element, idx) => {
    const outliers = Array.isArray(element.outliers) ? element.outliers : [];

    // Precomputed box traces expect one inner sample array per box. Each box
    // is placed at its own category — without `x`, plotly draws every trace at
    // position 0 and the boxes land on top of one another.
    const color = seriesColor(palette, idx);
    return {
      type: 'box',
      name: element.label,
      x: [element.label],
      q1: [element.first_quartile],
      median: [element.median],
      q3: [element.third_quartile],
      lowerfence: [element.min],
      upperfence: [element.max],
      boxpoints: outliers.length > 0 ? 'outliers' : false,
      marker: {
        color,
        outliercolor: 'rgba(219, 64, 82, 0.6)',
        line: { outliercolor: 'rgba(219, 64, 82, 1.0)', outlierwidth: 2 }
      },
      // The box, whiskers and median are drawn in the full colour over a
      // translucent fill of the same colour, so the median stays visible.
      line: { color, width: 2 },
      fillcolor: withAlpha(color, 0.45),
      ...(outliers.length > 0 ? { y: [outliers] } : {})
    };
  });

  // Each box is its own category and is named on the axis beneath it, so a
  // legend would repeat what the chart already says.
  const layout = buildLayout({
    axes: buildAxes({
      categoryLabel: chartData.x_label,
      measureLabel: chartData.y_label,
      categoryUnit: chartData.x_unit,
      measureUnit: chartData.y_unit,
      categoryRotation: chartData.x_axis_rotation || 0,
      measureRotation: chartData.y_axis_rotation || 0,
      showGrid,
      autoScale: chartData.auto_scale,
      ink
    }),
    showLegend: false,
    ink
  });

  return { data, layout };
};

// `showTitle: false` is for a frame that prints the title itself (a View
// card); the export still carries it. `ancestorTitles` are the titles of the
// combined charts this one sits inside, printed on the export as a prefix.
function BoxAndWhiskerChartComponent({ chartData, showTitle = true, ancestorTitles = [] }) {
  const theme = useTheme();
  // The series palette and the reading ink both come from the app theme, so the
  // chart layer holds no colour of its own.
  const ink = { primary: theme.palette.text.primary, secondary: theme.palette.text.secondary };
  const { data, layout } = buildBoxFigure(chartData, { ink, themePalette: theme.palette.custom.chartColors });
  const title = exportTitle(ancestorTitles, chartData.title);

  return (
    <ChartCard
      title={showTitle ? chartData.title : null}
      ink={ink}
      actions={<ChartExportButton onExport={() => downloadChartPng({ data, layout, title })} />}
    >
      <Plot
        data={data}
        layout={layout}
        config={CHART_CONFIG}
        style={{ width: '100%', height: 'calc(100% - 50px)' }}
        useResizeHandler={true}
      />
    </ChartCard>
  );
}

export default BoxAndWhiskerChartComponent;
